// Sheltered spots for the coming wind (ReelHunt Plus): where a net can stay out tonight, or where to fish now.
// No DOM here, so test/shelter.test.ts can run it in Node.
//
// How: the water areas around you (lakes, rivers, sea; water-tiles.ts) are drawn onto a grid of
// 50 m cells. For each shore cell, "fetch" is how far the wind travels over open water before it reaches the spot:
// the shorter, the smaller the waves. Exposure = wind speed × √fetch (km), taken at the worst hour of the time window
// and over the wind direction ±15°. The calmest shore spots win, nearer ones a little ahead.
import { compassIndex } from "../../shared/strings.ts";

export interface WindHour { time: number; speed: number; dir: number; gust: number }
export interface LatLon { lat: number; lon: number }
/** One water area: its outline and islands as rings. `flowing`: a river (nets are not set there). */
export interface WaterArea { rings: LatLon[][]; flowing?: boolean }

const CELL = 50;               // m
const MAX_FETCH = 6_000;       // m; beyond this more open water changes the waves little on Finnish lakes
const MIN_OPEN = 400;          // m; a spot needs this much open water in some direction (not a ditch or narrow river)

/** Water and land on a square grid around a centre. water: 1 = water; flowing: 1 = river. Row 0 is the southern edge. */
export interface Grid { n: number; cell: number; center: LatLon; kx: number; ky: number; water: Uint8Array; flowing: Uint8Array }

export function makeGrid(center: LatLon, radiusM: number): Grid {
  const n = Math.ceil((2 * radiusM) / CELL);
  return { n, cell: CELL, center, kx: 111_320 * Math.cos((center.lat * Math.PI) / 180), ky: 110_540, water: new Uint8Array(n * n), flowing: new Uint8Array(n * n) };
}
/** Metres east/north of the grid's south-west corner. */
const toXY = (g: Grid, p: LatLon): [number, number] =>
  [(p.lon - g.center.lon) * g.kx + (g.n * g.cell) / 2, (p.lat - g.center.lat) * g.ky + (g.n * g.cell) / 2];
const toLatLon = (g: Grid, x: number, y: number): LatLon =>
  ({ lat: g.center.lat + (y - (g.n * g.cell) / 2) / g.ky, lon: g.center.lon + (x - (g.n * g.cell) / 2) / g.kx });

/** Fills one area (all its rings, outer and inner) with the even-odd rule, so islands stay land. */
function fillArea(g: Grid, rings: LatLon[][], flowing: boolean): void {
  const rows: number[][] = Array.from({ length: g.n }, () => []);
  for (const ring of rings) {
    for (let i = 0; i + 1 < ring.length; i++) {
      const [x1, y1] = toXY(g, ring[i]), [x2, y2] = toXY(g, ring[i + 1]);
      if (y1 === y2) continue;
      const lo = Math.max(0, Math.ceil(Math.min(y1, y2) / g.cell - 0.5)), hi = Math.min(g.n - 1, Math.floor(Math.max(y1, y2) / g.cell - 0.5));
      for (let r = lo; r <= hi; r++) {
        const yc = (r + 0.5) * g.cell;
        if (yc < Math.min(y1, y2) || yc >= Math.max(y1, y2)) continue;
        rows[r].push(x1 + ((yc - y1) / (y2 - y1)) * (x2 - x1));
      }
    }
  }
  for (let r = 0; r < g.n; r++) {
    const xs = rows[r].sort((a, b) => a - b);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const a = Math.max(0, Math.ceil(xs[i] / g.cell - 0.5)), b = Math.min(g.n - 1, Math.floor(xs[i + 1] / g.cell - 0.5));
      for (let c = a; c <= b; c++) { g.water[r * g.n + c] = 1; if (flowing) g.flowing[r * g.n + c] = 1; }
    }
  }
}

/** Metres of open water upwind of cell (r, c), the wind coming from `fromDeg` (0 = north). */
export function fetchAt(g: Grid, r: number, c: number, fromDeg: number, max = MAX_FETCH): number {
  const a = (fromDeg * Math.PI) / 180, dx = Math.sin(a), dy = Math.cos(a), step = g.cell;
  let x = (c + 0.5) * g.cell, y = (r + 0.5) * g.cell;
  for (let d = 0; d < max; d += step) {
    x += dx * step; y += dy * step;
    const cc = Math.floor(x / g.cell), rr = Math.floor(y / g.cell);
    if (cc < 0 || rr < 0 || cc >= g.n || rr >= g.n) return max; // open water runs off the map: assume a long way
    if (!g.water[rr * g.n + cc]) return d;
  }
  return max;
}

/** A past catch near which a spot is more promising: your own (weight 1), a friend's shared one (0,5), more for nets. */
export interface HistoryPoint extends LatLon { weight: number; shared?: boolean }
export interface ShelterSpot extends LatLon {
  /** Wind speed × √(fetch km) at the worst hour: under ~3 sheltered, over ~8 rough. */
  exposure: number;
  /** Open water upwind at the worst hour, m. */
  fetch: number;
  /** The wind at the worst hour. */
  worst: WindHour;
  /** How the spot fares in each wind of the window (windSpells): the worst exposure while the wind is from that direction. */
  bySpell: { sector: number; from: number; exposure: number }[];
  km: number;
  /** Past catches within 400 m: your own and friends' shared ones. */
  own: number;
  shared: number;
}
export type ShelterLevel = "good" | "ok" | "rough";
export const shelterLevel = (exposure: number): ShelterLevel => (exposure < 3 ? "good" : exposure < 8 ? "ok" : "rough");

const HISTORY_M = 400;

/** The calmest shore spots within `searchM` of the centre for the given hours, spread out: at least 700 m apart, and
 *  a fifth of the search distance for longer searches (2 km apart at 10 km), so a boat search covers the whole area.
 *  `stillOnly`: only lakes and the sea, no rivers (for nets). `history`: past catches; among equally sheltered spots
 *  those near them come first, and the catch places themselves are candidates too (also out on open water, from a boat). */
export function findSheltered(g: Grid, hours: WindHour[], opts: { searchM?: number; count?: number; stillOnly?: boolean; history?: HistoryPoint[] } = {}): ShelterSpot[] {
  const searchM = opts.searchM ?? 6000, count = opts.count ?? 5, half = (g.n * g.cell) / 2;
  if (!hours.length) return [];
  // the rays to measure: each hour's wind direction ±15°, rounded to 5° so hours with nearly the same wind share them
  const round5 = (d: number) => ((Math.round(d / 5) * 5) % 360 + 360) % 360;
  const rays = [...new Set(hours.flatMap((h) => [-15, 0, 15].map((o) => round5(h.dir + o))))];
  const rayOf = new Map(rays.map((d, i) => [d, i]));
  const hourRays = hours.map((h) => [-15, 0, 15].map((o) => rayOf.get(round5(h.dir + o))!));
  const fetches = new Float64Array(rays.length);
  // the winds of the window, so each spot can say how it fares in every one of them, not only the worst
  const spells = windSpells(hours), spellOf = hours.map((h) => spells.findIndex((s) => s.hours.includes(h)));

  // past catches in grid metres, bucketed by 400 m squares for quick lookups
  const hist = (opts.history ?? []).map((p) => { const [x, y] = [(p.lon - g.center.lon) * g.kx + half, (p.lat - g.center.lat) * g.ky + half]; return { ...p, x, y }; })
    .filter((p) => p.x >= 0 && p.y >= 0 && p.x < 2 * half && p.y < 2 * half);
  const buckets = new Map<string, typeof hist>();
  const bkey = (x: number, y: number) => Math.floor(x / HISTORY_M) + "," + Math.floor(y / HISTORY_M);
  for (const p of hist) { const k = bkey(p.x, p.y); if (!buckets.has(k)) buckets.set(k, []); buckets.get(k)!.push(p); }
  const nearby = (x: number, y: number) => {
    let weight = 0, own = 0, shared = 0;
    const bx = Math.floor(x / HISTORY_M), by = Math.floor(y / HISTORY_M);
    for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
      for (const p of buckets.get(bx + i + "," + (by + j)) ?? []) {
        if (Math.hypot(p.x - x, p.y - y) > HISTORY_M) continue;
        weight += p.weight;
        if (p.shared) shared++; else own++;
      }
    }
    return { weight, own, shared };
  };

  const evaluate = (r: number, c: number): ShelterSpot | null => {
    const x = (c + 0.5) * g.cell, y = (r + 0.5) * g.cell, dist = Math.hypot(x - half, y - half);
    if (dist > searchM) return null;
    // not a ditch: some direction must have open water
    let open = 0;
    for (let d = 0; d < 360 && open < MIN_OPEN; d += 45) open = Math.max(open, fetchAt(g, r, c, d, MIN_OPEN));
    if (open < MIN_OPEN) return null;
    let exposure = -1, worst = hours[0], worstFetch = 0;
    const bySpell = spells.map((s) => ({ sector: s.sector, from: s.from, exposure: 0 }));
    for (let i = 0; i < rays.length; i++) fetches[i] = fetchAt(g, r, c, rays[i]);
    for (let i = 0; i < hours.length; i++) {
      const h = hours[i], [a, b, cc] = hourRays[i], f = Math.max(fetches[a], fetches[b], fetches[cc]);
      const e = h.speed * Math.sqrt(Math.max(0.05, f / 1000));
      if (e > exposure) { exposure = e; worst = h; worstFetch = f; }
      const sp = bySpell[spellOf[i]];
      if (sp && e > sp.exposure) sp.exposure = e;
    }
    const { own, shared } = hist.length ? nearby(x, y) : { own: 0, shared: 0 };
    return { ...toLatLon(g, x, y), exposure, fetch: worstFetch, worst, bySpell, km: dist / 1000, own, shared };
  };
  const usable = (k: number) => g.water[k] && !(opts.stillOnly && g.flowing[k]);

  const scored: ShelterSpot[] = [];
  for (let r = 1; r < g.n - 1; r++) {
    for (let c = 1; c < g.n - 1; c++) {
      const k = r * g.n + c;
      if (!usable(k)) continue;
      // a shore cell: water with land next to it
      if (g.water[k - 1] && g.water[k + 1] && g.water[k - g.n] && g.water[k + g.n]) continue;
      const s = evaluate(r, c);
      if (s) scored.push(s);
    }
  }
  // the catch places themselves (the nearest usable water within 100 m, since GPS on the shore may land on land)
  for (const p of hist) {
    const r0 = Math.floor(p.y / g.cell), c0 = Math.floor(p.x / g.cell);
    let found: [number, number] | null = null;
    for (let d = 0; d <= 2 && !found; d++)
      for (let i = -d; i <= d && !found; i++) for (let j = -d; j <= d && !found; j++) {
        const r = r0 + i, c = c0 + j;
        if (r > 0 && c > 0 && r < g.n - 1 && c < g.n - 1 && usable(r * g.n + c)) found = [r, c];
      }
    const s = found && evaluate(found[0], found[1]);
    if (s) scored.push(s);
  }

  // First how sheltered (sheltered / fairly sheltered / windy: a windy spot never wins on history), then within the
  // same group the spots with past catches (more catches first), then calmer and nearer (at the edge of the area a spot
  // needs to be a third calmer). In light wind (under 3 m/s) every spot counts as sheltered, so history and nearness decide.
  const calm = Math.max(...hours.map((h) => h.speed)) < 3;
  const tier = (s: ShelterSpot) => (calm ? 0 : ["good", "ok", "rough"].indexOf(shelterLevel(s.exposure)));
  const xyOf = (s: LatLon): [number, number] => [(s.lon - g.center.lon) * g.kx + half, (s.lat - g.center.lat) * g.ky + half];
  const weightNear = (s: ShelterSpot) => (hist.length ? nearby(...xyOf(s)).weight : 0);
  const score = (s: ShelterSpot) => tier(s) * 1000 - Math.min(3, weightNear(s)) * 100 + (calm ? 1 : s.exposure) * (1 + (0.5 * s.km * 1000) / searchM);
  const scores = new Map(scored.map((s) => [s, score(s)]));
  scored.sort((a, b) => scores.get(a)! - scores.get(b)!);
  const picked: ShelterSpot[] = [];
  const minApart = Math.max(700, searchM / 5);
  const apart = (a: LatLon, b: LatLon) => Math.hypot((a.lat - b.lat) * g.ky, (a.lon - b.lon) * g.kx) >= minApart;
  for (const s of scored) {
    if (picked.length >= count) break;
    if (picked.every((p) => apart(p, s))) picked.push(s);
  }
  // a windy spot is no suggestion when there are sheltered ones: it's only shown when nothing better exists
  return picked.some((s) => shelterLevel(s.exposure) !== "rough") ? picked.filter((s) => shelterLevel(s.exposure) !== "rough") : picked;
}

/** The hours to look at: "tonight" (20–08, or from now if it's already evening) or "now" (the next 3 hours). */
export function windowHours(all: WindHour[], kind: "night" | "now", now = Date.now()): WindHour[] {
  if (kind === "now") return all.filter((h) => h.time >= now - 36e5 && h.time <= now + 3 * 36e5);
  const d = new Date(now), hr = d.getHours();
  const start = new Date(d); start.setHours(20, 0, 0, 0);
  if (hr < 8) start.setDate(start.getDate() - 1);
  const end = new Date(start); end.setDate(end.getDate() + 1); end.setHours(8, 0, 0, 0);
  const from = Math.max(start.getTime(), now - 36e5);
  return all.filter((h) => h.time >= from && h.time <= end.getTime());
}

/** A stretch of hours with the wind from one compass direction (8 points). */
export interface WindSpell { from: number; sector: number; lo: number; hi: number; hours: WindHour[] }
/** The wind over the hours as up to `max` spells, so a turning wind reads e.g. "SE until midnight, then W, then SW".
 *  A single hour from another direction is folded into its neighbour (forecast wobble), as is the shortest spell
 *  while there are more than `max`. */
export function windSpells(hours: WindHour[], max = 3): WindSpell[] {
  const make = (hs: WindHour[], sector: number): WindSpell =>
    ({ from: hs[0].time, sector, lo: Math.min(...hs.map((h) => h.speed)), hi: Math.max(...hs.map((h) => h.speed)), hours: hs });
  let spells: WindSpell[] = [];
  for (const h of hours) {
    const s = compassIndex(h.dir), last = spells.at(-1);
    if (last && last.sector === s) spells[spells.length - 1] = make([...last.hours, h], s); else spells.push(make([h], s));
  }
  const merge = (i: number) => {
    // into the neighbour whose direction is closer (on ties the longer one)
    const gap = (j: number) => (j < 0 || j >= spells.length ? Infinity : Math.min((spells[i].sector - spells[j].sector + 8) % 8, (spells[j].sector - spells[i].sector + 8) % 8));
    const into = gap(i - 1) < gap(i + 1) || (gap(i - 1) === gap(i + 1) && (spells[i - 1]?.hours.length ?? 0) >= (spells[i + 1]?.hours.length ?? 0)) ? i - 1 : i + 1;
    const [a, b] = into < i ? [spells[into], spells[i]] : [spells[i], spells[into]];
    const keep = spells[into].sector;
    spells.splice(Math.min(i, into), 2, make([...a.hours, ...b.hours], keep));
    // neighbours that now share a direction become one
    spells = spells.reduce<WindSpell[]>((out, s) => { const p = out.at(-1); if (p && p.sector === s.sector) out[out.length - 1] = make([...p.hours, ...s.hours], s.sector); else out.push(s); return out; }, []);
  };
  for (let i; spells.length > 1 && (i = spells.findIndex((s) => s.hours.length === 1)) >= 0;) merge(i);
  while (spells.length > max) merge(spells.reduce((m, s, i) => (s.hours.length < spells[m].hours.length ? i : m), 0));
  return spells;
}

/** Draws water areas onto the grid. */
export function fillAreas(g: Grid, areas: WaterArea[]): void {
  for (const a of areas) fillArea(g, a.rings, !!a.flowing);
}
